import React, { useCallback, useRef, useState } from "react";
import styled, { css } from "styled-components";

import ContentTag from "./ContentTag";

/**
 * ContentTag のチップ + インライン入力型エディタ。
 *
 * - Enter / カンマ / 読点 / 空白 / 改行で確定してチップ化
 * - チップの × クリック、または空欄で Backspace すると末尾タグを削除
 * - ペースト文字列は区切り文字で分割して一括登録
 * - 正規化: 先頭 `#` 除去（旧環境ハッシュタグ互換）・前後空白除去・
 *   60 字切り詰め（BE の `HashTag.tag_name` max_length と一致）・重複排除
 * - IME 変換中の Enter では確定しない（composition イベントで判定）
 *
 * 制御コンポーネント: 確定済みタグは `value` / `onChange` で親が持つ。
 *
 * 表のセル内で使う場合は `variant="bare"` を指定して枠と余白を外す
 * （セルのグリッド線の中にさらに枠が入る「枠の中の枠」を避ける）。
 */
export interface ContentTagInputProps {
  /** 確定済みタグ（正規化済みを想定。表示前にも正規化する）。 */
  value: string[];
  // eslint-disable-next-line no-unused-vars
  onChange: (next: string[]) => void;
  /** フィールドラベル（グローバル Input と同じ見た目で表示）。 */
  label?: string;
  placeholder?: string;
  /** タグ 1 件の最大文字数。既定 60（BE の max_length と一致）。 */
  maxTagLength?: number;
  /**
   * `bare` にすると枠線・内側 padding を外し、素のインライン編集にする。
   * 表のセル内で使うときに指定する（セルのグリッド線の中にさらに枠が入る
   * 「枠の中の枠」を避けるため。編集中のフィードバックは `Table` 側の
   * `td:focus-within` の青いリングが担う）。
   */
  variant?: "field" | "bare";
  className?: string;
}

/** タグ確定の区切り文字（空白 / カンマ / 読点 / 改行）。旧 `splitTagsInput` と同一。 */
const SEPARATOR_RE = /[\s,、\n]+/;

/** 先頭の `#` と前後空白を除去（`utils/profileMedia.stripTagHash` と同一仕様）。 */
const normalizeTag = (raw: string, maxLength: number): string =>
  raw.replace(/^#+/, "").trim().slice(0, maxLength);

const Field = styled.div<{ $bare: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  cursor: text;

  /* field: グローバル input（soremo_style.scss）と同じ枠・フォーカス演出。
     bare: 枠と余白を持たず、置かれた器（表のセル等）に馴染ませる。 */
  ${({ $bare }) =>
    $bare
      ? css`
          padding: 0;
        `
      : css`
          padding: 8px 12px;
          border: 1px solid var(--soremo-border);
          border-radius: 4px;

          &:focus-within {
            border-color: var(--soremo-blue);
            box-shadow: 2px 4px 8px 3px rgba(0, 154, 206, 0.1);
          }
        `}
`;

const InlineInput = styled.input`
  /* type 属性を付けないことで soremo_style.scss の input[type="text"]
   * スタイル（枠・padding）を回避し、素のインライン入力にする。 */
  flex: 1;
  min-width: 96px;
  border: none;
  outline: none;
  margin: 0;
  padding: 4px 0;
  background: transparent;
  line-height: 150%;

  &:focus {
    /* SP での自動ズームを回避（グローバル input:focus と同じ対策） */
    font-size: 16px;
  }
`;

const ContentTagInput: React.FC<ContentTagInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  maxTagLength = 60,
  variant = "field",
  className,
}) => {
  const [pending, setPending] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  /** pending テキスト（複数区切りを含みうる）を確定してチップ化する。 */
  const commitText = useCallback(
    (text: string) => {
      const parts = text
        .split(SEPARATOR_RE)
        .map((t) => normalizeTag(t, maxTagLength))
        .filter(Boolean);
      if (parts.length === 0) return;
      const next = [...value];
      const seen = new Set(next);
      parts.forEach((t) => {
        if (!seen.has(t)) {
          seen.add(t);
          next.push(t);
        }
      });
      if (next.length !== value.length) onChange(next);
    },
    [value, onChange, maxTagLength],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    // IME 変換中は区切り文字判定をしない（変換途中の読点等で確定しない）。
    if (!isComposing && SEPARATOR_RE.test(text)) {
      // 末尾が区切り文字でなければ、最後のトークンは入力継続中として残す。
      const endsWithSeparator = /[\s,、\n]+$/.test(text);
      const tokens = text.split(SEPARATOR_RE);
      const rest = endsWithSeparator ? "" : (tokens.pop() ?? "");
      commitText(tokens.join(" "));
      setPending(rest);
      return;
    }
    setPending(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // IME 変換確定の Enter では確定しない。
    if (e.key === "Enter" && !isComposing && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commitText(pending);
      setPending("");
      return;
    }
    if (e.key === "Backspace" && pending === "" && value.length > 0) {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    setIsComposing(false);
    // 変換確定後のテキストに区切り文字が含まれていれば確定する。
    const text = e.currentTarget.value;
    if (SEPARATOR_RE.test(text)) {
      const endsWithSeparator = /[\s,、\n]+$/.test(text);
      const tokens = text.split(SEPARATOR_RE);
      const rest = endsWithSeparator ? "" : (tokens.pop() ?? "");
      commitText(tokens.join(" "));
      setPending(rest);
    }
  };

  const handleBlur = () => {
    if (pending.trim()) {
      commitText(pending);
      setPending("");
    }
  };

  const handleRemove = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  const displayTags = value.map((t) => normalizeTag(t, maxTagLength)).filter(Boolean);

  return (
    <div className={`u-col u-w100 u-gap4 ${className ?? ""}`}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">{label}</label>
      )}
      <Field
        $bare={variant === "bare"}
        onClick={() => inputRef.current?.focus()}
      >
        {displayTags.map((tag) => (
          <ContentTag key={tag} onRemove={() => handleRemove(tag)} removeLabel={`${tag} を削除`}>
            {tag}
          </ContentTag>
        ))}
        <InlineInput
          ref={inputRef}
          value={pending}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={handleCompositionEnd}
          onBlur={handleBlur}
          placeholder={displayTags.length === 0 ? placeholder : undefined}
          aria-label={label ?? "タグを入力"}
        />
      </Field>
    </div>
  );
};

export default ContentTagInput;
